import type { Measurement } from '../lib/api';
import { compareByNetwork } from '../lib/compare';
import { cardinalIt } from '../lib/geo';
import { NETWORKS, NETWORK_ORDER, type Network } from '../lib/operators';
import { h, fmtDeg, toast } from './dom';
import { getState, setState, subscribe } from './state';
import { saveMeasurement, deleteMeasurement } from './data';

const DEVICES = ['iPhone 12', 'iPhone 13', 'Pixel 2', 'UniFi'];
const DEVICE_KEY = 'antenne.device';

function rememberedDevice(): string {
  try { return localStorage.getItem(DEVICE_KEY) ?? 'iPhone 12'; } catch { return 'iPhone 12'; }
}
function rememberDevice(d: string) {
  try { localStorage.setItem(DEVICE_KEY, d); } catch { /* private mode: ignore */ }
}

/** "−95", "-95", "12,5" → number; empty → undefined; garbage → NaN. */
export function parseNum(v: string): number | undefined {
  const t = v.trim().replace(',', '.').replace('−', '-');
  if (!t) return undefined;
  return Number(t);
}

function allMeasurements(): (Measurement & { pending?: boolean })[] {
  const st = getState();
  const pendingIds = new Set(st.pending.map(p => p.id));
  return [...st.pending.map(p => ({ ...p, pending: true })), ...st.measurements.filter(m => !pendingIds.has(m.id))].sort((a, b) =>
    b.takenAt.localeCompare(a.takenAt),
  );
}

function form(onDone: () => void) {
  const d = getState().draft ?? {};
  const device = h('select', {}, ...DEVICES.map(x => h('option', { value: x, selected: x === rememberedDevice() }, x)));
  const network = h('select', {}, ...NETWORK_ORDER.map(n => h('option', { value: n, selected: n === (d.network ?? 'TIM') }, n)));
  const radio = h('select', {}, h('option', { value: 'NR' }, '5G (NR)'), h('option', { value: 'LTE' }, '4G (LTE)'));
  const num = (ph: string, val?: number) => h('input', { type: 'text', inputmode: 'text', placeholder: ph, value: val === undefined ? '' : String(val), autocomplete: 'off' });
  const rsrp = num('es. -95');
  const sinr = num('es. 12');
  const rsrq = num('es. -11');
  const band = h('input', { type: 'text', placeholder: 'es. n78', autocomplete: 'off' });
  const cell = h('input', { type: 'text', placeholder: 'PCI o ID cella', autocomplete: 'off' });
  const down = num('Mbps');
  const up = num('Mbps');
  const heading = num('gradi', d.headingDeg !== undefined ? Math.round(d.headingDeg) : undefined);
  const spot = h('input', { type: 'text', placeholder: 'es. tetto, vicino al palo', autocomplete: 'off' });
  const note = h('textarea', { rows: 2 });

  const f = h('form', {
    class: 'card form',
    onsubmit: async (e: Event) => {
      e.preventDefault();
      const nums = { rsrp: parseNum(rsrp.value), sinr: parseNum(sinr.value), rsrq: parseNum(rsrq.value), downMbps: parseNum(down.value), upMbps: parseNum(up.value), headingDeg: parseNum(heading.value) };
      const bad = Object.entries(nums).find(([, v]) => v !== undefined && !Number.isFinite(v));
      if (bad) { toast(`Valore non valido: ${bad[0]}`); return; }
      if (nums.rsrp === undefined && nums.sinr === undefined && nums.downMbps === undefined) { toast('Inserisci almeno RSRP, SINR o download'); return; }
      const m: Measurement = {
        id: crypto.randomUUID(),
        takenAt: new Date().toISOString(),
        network: network.value as Network,
        device: device.value,
        radio: radio.value,
        band: band.value.trim() || undefined,
        cellId: cell.value.trim() || undefined,
        ...Object.fromEntries(Object.entries(nums).filter(([, v]) => v !== undefined)),
        lat: d.lat,
        lon: d.lon,
        siteId: d.siteId,
        spot: spot.value.trim() || undefined,
        note: note.value.trim() || undefined,
      };
      rememberDevice(device.value);
      try {
        await saveMeasurement(m);
      } catch (err) {
        // Never drop a reading silently: keep the form filled so it can be retried or copied down.
        toast(`Misura NON salvata sul telefono (${(err as Error).name || 'errore'}): riprova o annotala.`);
        return;
      }
      setState({ draft: undefined });
      toast('Misura salvata');
      onDone();
    },
  },
    h('h2', {}, 'Nuova misura'),
    h('p', { class: 'muted small' }, 'iPhone: componi *3001#12345#* e chiama → “Serving Cell Info”. UniFi: pannello del dispositivo.'),
    h('div', { class: 'row' }, h('label', {}, 'Dispositivo', device), h('label', {}, 'Operatore', network)),
    h('div', { class: 'row' }, h('label', {}, 'Rete', radio), h('label', {}, 'Banda', band)),
    h('div', { class: 'row three' }, h('label', {}, 'RSRP (dBm)', rsrp), h('label', {}, 'SINR (dB)', sinr), h('label', {}, 'RSRQ (dB)', rsrq)),
    h('div', { class: 'row three' }, h('label', {}, 'Download', down), h('label', {}, 'Upload', up), h('label', {}, 'Direzione', heading)),
    h('label', {}, 'Cella', cell),
    h('label', {}, 'Punto', spot),
    h('label', {}, 'Note', note),
    d.lat !== undefined ? h('p', { class: 'muted small' }, 'Posizione GPS allegata.') : null,
    h('div', { class: 'actions' },
      h('button', { class: 'primary', type: 'submit' }, 'Salva'),
      h('button', { type: 'button', onclick: () => { setState({ draft: undefined }); onDone(); } }, 'Annulla'),
    ),
  );
  setTimeout(() => rsrp.focus(), 50);
  return f;
}

function quality(m: Measurement): string {
  const s = m.sinr;
  if (s === undefined) return '';
  return s >= 13 ? 'q-ok' : s >= 0 ? 'q-mid' : 'q-bad';
}

export function mountMeasures(root: HTMLElement) {
  let showForm = false;
  const body = h('div', { class: 'misure' });
  root.append(body);

  function render() {
    const st = getState();
    if (st.draft) showForm = true;
    const all = allMeasurements();
    const summary = compareByNetwork(all);
    const parts: (Node | null)[] = [
      h('div', { class: 'panel-head' },
        h('h2', {}, 'Misure'),
        showForm ? null : h('button', { class: 'primary', onclick: () => { showForm = true; render(); } }, '+ Nuova misura'),
      ),
      showForm ? form(() => { showForm = false; render(); }) : null,
      summary.length
        ? h('div', { class: 'card' },
            h('h3', {}, 'Confronto operatori'),
            h('p', { class: 'muted small' }, 'Vince il SINR più alto (qualità del segnale), a parità conta l’RSRP.'),
            h('table', { class: 'compare' },
              h('thead', {}, h('tr', {}, h('th', {}, 'Rete'), h('th', {}, 'Misure'), h('th', {}, 'RSRP'), h('th', {}, 'SINR'), h('th', {}, 'Down'))),
              h('tbody', {},
                ...summary.map((s, i) =>
                  h('tr', { class: i === 0 ? 'winner' : '' },
                    h('td', {}, h('span', { class: 'dot', style: `background:${NETWORKS[s.network].color}` }), ' ', s.network),
                    h('td', {}, String(s.count)),
                    h('td', {}, s.bestRsrp !== undefined ? String(s.bestRsrp) : '–'),
                    h('td', {}, s.bestSinr !== undefined ? String(s.bestSinr) : '–'),
                    h('td', {}, s.bestDown !== undefined ? String(s.bestDown) : '–'),
                  ),
                ),
              ),
            ),
          )
        : !showForm ? h('p', { class: 'muted' }, 'Nessuna misura. Da “Sul posto” usa “Salva misura qui” per allegare direzione e posizione.') : null,
      all.length ? h('h3', {}, 'Tutte le misure') : null,
      h('ul', { class: 'mlist' },
        ...all.map(m =>
          h('li', {},
            h('details', {},
              h('summary', {},
                h('span', { class: 'dot', style: `background:${NETWORKS[m.network]?.color ?? '#888'}` }),
                h('strong', {}, m.network),
                h('span', {}, m.radio === 'NR' ? '5G' : '4G'),
                h('span', { class: quality(m) }, [m.rsrp !== undefined && `${m.rsrp} dBm`, m.sinr !== undefined && `SINR ${m.sinr}`].filter(Boolean).join(' · ')),
                m.pending ? h('span', { class: 'pending' }, 'da sincronizzare') : null,
              ),
              h('p', { class: 'small' }, new Date(m.takenAt).toLocaleString('it-IT'), ' · ', m.device),
              h('p', { class: 'small' }, [
                m.band && `Banda ${m.band}`, m.cellId && `Cella ${m.cellId}`, m.rsrq !== undefined && `RSRQ ${m.rsrq}`,
                m.downMbps !== undefined && `↓ ${m.downMbps} Mbps`, m.upMbps !== undefined && `↑ ${m.upMbps} Mbps`,
                m.headingDeg !== undefined && `Direzione ${fmtDeg(m.headingDeg)} ${cardinalIt(m.headingDeg)}`,
                m.spot, m.note,
              ].filter(Boolean).join(' · ')),
              h('button', {
                class: 'danger small-btn',
                onclick: async () => {
                  if (!confirm('Eliminare questa misura?')) return;
                  try { await deleteMeasurement(m.id); } catch (e) { toast((e as Error).message); }
                },
              }, 'Elimina'),
            ),
          ),
        ),
      ),
    ];
    body.replaceChildren(...parts.filter((x): x is Node => x !== null));
  }

  subscribe((s, p) => {
    if (s.tab === 'misure' && (p.tab !== 'misure' || s.measurements !== p.measurements || s.pending !== p.pending || s.draft !== p.draft)) render();
  });
  if (getState().tab === 'misure') render();
}
